import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useState } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Main } from '@expo/html-elements';
import {
  BodyText,
  ModalFooter,
  TextLinkifyTags,
  theme,
  useBreakpointHelpers,
} from '@sg/garnish';

import { CheckoutLayout, CheckoutLayoutColumn } from '@order/components';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { PageWrapper } from '../../components/PageWrapper';
import type { SubscriptionCreditCard } from '../../graphql/types';
import { SweetpassCheckoutBlock } from './SweetpassCheckoutBlock';
import { SweetpassCheckoutCardBlock } from './SweetpassCheckoutCardBlock';
import { SweetpassCheckoutCta } from './SweetpassCheckoutCta';
import { SweetpassCheckoutHeader } from './SweetpassCheckoutHeader';
import type { SweetpassCheckoutPage } from './useSweetpassCheckoutLoadedContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreenContent = ({
  testID,
  content,
  creditCards,
  savedCardHandler,
  ctaHandler,
}: SweetpassCheckoutScreenContentProps) => {
  const { t } = useLocalizationContext();
  const { track } = useTelemetry();
  const navigation = useNavigation();
  const { matchByBreakpointIndex, isMobile } = useBreakpointHelpers();

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCta = useCallback(async () => {
    setIsLoading(true);
    await ctaHandler();
    setIsLoading(false);
  }, [ctaHandler]);

  const responsiveStyle = matchByBreakpointIndex([
    styles.wrapperMobile,
    styles.wrapperTablet,
    styles.wrapperDesktop,
  ]);
  const style = [styles.wrapper, responsiveStyle];

  const containerResponsiveStyle = matchByBreakpointIndex([
    {},
    styles.containerTabletUp,
    styles.containerDesktopUp,
  ]);
  const containerStyles = [styles.container, containerResponsiveStyle];

  const navigateToNewCreditCard = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'SweetpassPaymentMethodAdd',
    });
    track('sweetpass_account.add_payment_cta.tapped');
  }, [navigation, track]);

  const handleAcceptTerms = useCallback(
    (toggle: boolean) => {
      setHasAcceptedTerms(toggle);

      if (toggle) track('sweetpass_account.terms.viewed');
    },
    [track],
  );

  // Render Todo List with two buttons, edit and remove
  return (
    <>
      <PageWrapper
        withHeaderTitle
        withoutFooter
        contentContainerStyle={style}
        testID="sg-sweetpass-checkout-wrapper"
      >
        <Main testID={testID} style={containerStyles}>
          <SweetpassCheckoutHeader />
          <CheckoutLayout>
            <CheckoutLayoutColumn>
              <SweetpassCheckoutCardBlock
                creditCards={creditCards}
                savedCardHandler={savedCardHandler}
                onReplaceCreditCardBtnPress={navigateToNewCreditCard}
                onAddCreditCardBtnPress={navigateToNewCreditCard}
              />
            </CheckoutLayoutColumn>
            <CheckoutLayoutColumn>
              <SweetpassCheckoutBlock
                content={content}
                isLoading={isLoading}
                handleCta={handleCta}
                handleAcceptTerms={handleAcceptTerms}
                hasAcceptedTerms={hasAcceptedTerms}
              />
            </CheckoutLayoutColumn>
          </CheckoutLayout>
        </Main>
      </PageWrapper>
      {isMobile ? (
        <SafeAreaView edges={['bottom']}>
          <ModalFooter>
            <SweetpassCheckoutCta
              testID="sweetpass.checkout.cta-mobile"
              handleCta={handleCta}
              isLoading={isLoading}
              hasAccepted={hasAcceptedTerms}
            />
            <BodyText style={styles.upgradeNotice} size={5}>
              <TextLinkifyTags
                size={5}
                links={[
                  {
                    tag: 'terms',
                    description: t(
                      'sweetpass.checkout.upgrade-cta-notice.terms-description',
                    ),
                    href: t('sweetpass-upgrade.confirm-modal.terms-url'),
                    newTab: true,
                  },
                ]}
              >
                {t('sweetpass.checkout.upgrade-cta-notice')}
              </TextLinkifyTags>
            </BodyText>
          </ModalFooter>
        </SafeAreaView>
      ) : null}
    </>
  );
};

type SweetpassCheckoutScreenContentProps = {
  readonly content: SweetpassCheckoutPage;
  readonly creditCards?: readonly SubscriptionCreditCard[];
  readonly savedCardHandler: Dispatch<SetStateAction<string>>;
  readonly ctaHandler: (() => Promise<void>) | (() => void);
} & Pick<ViewProps, 'testID'>;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
    backgroundColor: theme.colors.GREEN_2,
  },
  wrapperMobile: {
    backgroundColor: theme.colors.OATMEAL,
  },
  wrapperTablet: {
    paddingTop: theme.spacing.large,
  },
  wrapperDesktop: {
    paddingTop: theme.spacing.xlarge,
  },
  container: {
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: theme.spacing.medium,
    paddingBottom: theme.spacing.large,
  },
  containerTabletUp: {
    maxWidth: 500,
  },
  containerDesktopUp: {
    maxWidth: 1024,
  },
  upgradeNotice: {
    paddingTop: theme.spacing.small,
    textAlign: 'center',
    color: theme.colors.NEUTRAL_2,
  },
});
